import React, { useRef, useCallback } from 'react';
import { cloneDeep, isEmpty, compact, isEqual } from 'lodash';
import { useClassicViewStore } from './classicViewStore';

const useClassicViewHook = ({ module, viewId, defaultOptions = {} }) => {
  const {
    setGlobalStateConditions,
    setSelectedItem,
    setListData,
    setListInfo,
    setSearchConditions,
    setFilterConditions,
    setSortConditions,
    setRecords,
    getGlobalStateConditions,
    getSelectedItem,
    getListData,
    getListInfo,
    getSearchConditions,
    getFilterConditions,
    getSortConditions,
    getRecords,
    clearAllModuleData,
    clearModuleWithViewId,
    resetAllFilters
  } = useClassicViewStore();

  const viewIdRef = useRef(null);
  viewIdRef.current = viewId;

  // Get current state
  const currentGlobalConditions = getGlobalStateConditions(
    module,
    viewIdRef.current
  );
  const currentSelectedItem = getSelectedItem(module, viewIdRef.current);
  const currentListData = getListData(module, viewIdRef.current);
  const currentListInfo = getListInfo(module, viewIdRef.current);
  const currentSearchConditions = getSearchConditions(
    module,
    viewIdRef.current
  );
  const currentFilterConditions = getFilterConditions(
    module,
    viewIdRef.current
  );
  const currentSortConditions = getSortConditions(module, viewIdRef.current);
  const currentRecords = getRecords(module, viewIdRef.current);

  // Initialize default global conditions if not exists
  const initializeGlobalConditions = useCallback(
    (activeView) => {
      if (!currentGlobalConditions) {
        const defaultGlobalConditions = {
          name: activeView?.id,
          filterConditions: [],
          searchConditions: [],
          pageNumber: 1,
          sort: defaultOptions?.sort || [
            {
              attribute: 'createdTime',
              order: 'DESC'
            }
          ],
          personalizedSort: defaultOptions?.sort || [
            {
              attribute: 'createdTime',
              order: 'DESC'
            }
          ],
          conditions: null
        };
        setGlobalStateConditions(
          module,
          viewIdRef.current,
          defaultGlobalConditions
        );
        return defaultGlobalConditions;
      }
      return currentGlobalConditions;
    },
    [currentGlobalConditions, defaultOptions, module, setGlobalStateConditions]
  );

  // Update global state conditions
  const updateGlobalStateConditions = useCallback(
    (updatedConditions) => {
      setGlobalStateConditions(module, viewIdRef.current, updatedConditions);
    },
    [module, setGlobalStateConditions]
  );

  // Update selected item
  const updateSelectedItem = useCallback(
    (item) => {
      setSelectedItem(module, viewIdRef.current, item);
    },
    [module, setSelectedItem]
  );

  // Update list data
  const updateListData = useCallback(
    (data) => {
      setListData(module, viewIdRef.current, data);
    },
    [module, setListData]
  );

  // Update list info
  const updateListInfo = useCallback(
    (info) => {
      setListInfo(module, viewIdRef.current, info);
    },
    [module, setListInfo]
  );

  // Update records
  const updateRecords = useCallback(
    (records) => {
      setRecords(module, viewIdRef.current, records);
    },
    [module, setRecords]
  );

  // Handle search functionality
  const handleSearch = useCallback(
    (searchText, searchTerms, setViewConditionsCallback) => {
      const globalConditions =
        currentGlobalConditions || initializeGlobalConditions();

      if (!searchText) {
        // Clear search - simple reset without touching filter conditions
        const updatedConditions = {
          ...globalConditions,
          searchConditions: [],
          conditions: isEmpty(globalConditions.filterConditions)
            ? null
            : {
                joinOperator: 'AND',
                operands: globalConditions.filterConditions
              }
        };

        updateGlobalStateConditions(updatedConditions);
        // setSearchConditions(module, viewIdRef.current, []);
        setViewConditionsCallback(updatedConditions.conditions);
        return;
      }

      const listCondition = {
        joinOperator: 'OR',
        operands: searchTerms.map((term) => ({
          attribute: term,
          operator: 'contains',
          value: searchText
        }))
      };

      const operands = compact(
        [listCondition].concat(
          globalConditions.filterConditions?.operands || []
        )
      ).filter((obj) => !isEmpty(obj));

      const complexCondition = {
        joinOperator: 'AND',
        operands
      };

      const updatedConditions = {
        ...globalConditions,
        searchConditions: listCondition,
        conditions: complexCondition
      };

      updateGlobalStateConditions(updatedConditions);
      // setSearchConditions(module, viewIdRef.current, listCondition);
      setViewConditionsCallback(complexCondition);
    },
    [
      currentGlobalConditions,
      initializeGlobalConditions,
      module,
      updateGlobalStateConditions,
      setSearchConditions
    ]
  );

  // Handle filter functionality
  const handleFilter = useCallback(
    (filterConditions) => {
      const globalConditions =
        currentGlobalConditions || initializeGlobalConditions();

      const operands = compact(
        [globalConditions.searchConditions || {}].concat(
          filterConditions?.operands || []
        )
      ).filter((obj) => !isEmpty(obj));

      const complexCondition = isEmpty(operands)
        ? null
        : {
            joinOperator: 'AND',
            operands
          };

      const updatedConditions = {
        ...globalConditions,
        filterConditions: !isEmpty(filterConditions?.operands)
          ? filterConditions
          : [],
        conditions: complexCondition,
        pageNumber: 1
      };

      updateGlobalStateConditions(updatedConditions);
      setFilterConditions(module, viewIdRef.current, filterConditions);
    },
    [
      currentGlobalConditions,
      initializeGlobalConditions,
      module,
      updateGlobalStateConditions,
      setFilterConditions
    ]
  );

  // Handle sort functionality
  const handleSort = useCallback(
    (sortConditions) => {
      const globalConditions =
        currentGlobalConditions || initializeGlobalConditions();

      const updatedConditions = {
        ...globalConditions,
        sort: sortConditions,
        personalizedSort: sortConditions
      };

      updateGlobalStateConditions(updatedConditions);
      setSortConditions(module, viewIdRef.current, sortConditions);
    },
    [
      currentGlobalConditions,
      initializeGlobalConditions,
      module,
      updateGlobalStateConditions,
      setSortConditions
    ]
  );

  // Reset filters
  const resetFilters = useCallback(
    (type = 'all', setViewConditionsCallback) => {
      const globalConditions =
        currentGlobalConditions || initializeGlobalConditions();

      switch (type) {
        case 'filter':
          // Check if search and filter conditions are identical when resetting filters
          let updatedFilterConditions = [];

          const filterResetConditions = {
            ...globalConditions,
            filterConditions: updatedFilterConditions,
            conditions: isEmpty(globalConditions.searchConditions)
              ? isEmpty(updatedFilterConditions)
                ? null
                : {
                    joinOperator: 'AND',
                    operands: updatedFilterConditions
                  }
              : isEmpty(updatedFilterConditions)
              ? {
                  joinOperator: 'AND',
                  operands: [globalConditions.searchConditions]
                }
              : {
                  joinOperator: 'AND',
                  operands: [
                    globalConditions.searchConditions,
                    ...updatedFilterConditions
                  ]
                }
          };

          updateGlobalStateConditions(filterResetConditions);
          setFilterConditions(
            module,
            viewIdRef.current,
            updatedFilterConditions
          );
          setViewConditionsCallback &&
            setViewConditionsCallback(filterResetConditions?.conditions);

          break;

        case 'search':
          // Check if search and filter conditions are identical when resetting search
          let updatedFilterConditionsForSearch =
            globalConditions.filterConditions?.operands || [];

          const searchResetConditions = {
            ...globalConditions,
            searchConditions: [],
            conditions: isEmpty(updatedFilterConditionsForSearch)
              ? null
              : {
                  joinOperator: 'AND',
                  operands: updatedFilterConditionsForSearch.filter(
                    (condition) => !isEmpty(condition)
                  )
                }
          };

          updateGlobalStateConditions(searchResetConditions);
          setSearchConditions(module, viewIdRef.current, []);
          setFilterConditions(
            module,
            viewIdRef.current,
            updatedFilterConditionsForSearch
          );
          setViewConditionsCallback(searchResetConditions.conditions);
          break;

        default:
          resetAllFilters(module, viewIdRef.current);
          break;
      }
    },
    [
      currentGlobalConditions,
      initializeGlobalConditions,
      module,
      updateGlobalStateConditions,
      setFilterConditions,
      setSearchConditions,
      resetAllFilters
    ]
  );

  // Clear all data for current module
  const clearModuleData = useCallback(() => {
    clearAllModuleData(module);
  }, [module, clearAllModuleData]);

  // Clear data for current view
  const clearViewData = useCallback(() => {
    clearModuleWithViewId(module, viewIdRef.current);
  }, [module, clearModuleWithViewId]);

  return {
    // State
    globalStateConditions: currentGlobalConditions,
    selectedItem: currentSelectedItem,
    listData: currentListData,
    listInfo: currentListInfo,
    searchConditions: currentSearchConditions,
    filterConditions: currentFilterConditions,
    sortConditions: currentSortConditions,
    selectedRecords: currentRecords || [],

    // Actions
    initializeGlobalConditions,
    updateGlobalStateConditions,
    updateSelectedItem,
    updateListData,
    updateListInfo,
    updateRecords,
    handleSearch,
    handleFilter,
    handleSort,
    resetFilters,
    clearModuleData,
    clearViewData,

    // Utilities
    showResetFilter:
      !isEmpty(currentGlobalConditions?.filterConditions) ||
      !isEmpty(currentGlobalConditions?.searchConditions)
  };
};

export default useClassicViewHook;
